import { BrowserModule, DomSanitizer, Title } from '@angular/platform-browser';
import { NgModule, LOCALE_ID } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CeProvidersModule } from './providers/providers.module';

import { LayoutModule } from 'layout/layout.module';
import { MatIconRegistry, MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ChargeyeTranslateLoader } from './localization/translate-loader';
import { StationsServiceModule } from 'shared/stations-service/stations.module';
import { setupIcons } from 'icons';
import { APP_BASE_HREF, PlatformLocation, registerLocaleData } from '@angular/common';
import { LocaleModule } from 'shared/locale/locale.module';
import { LocationStatusComponent } from 'locations/location-dialog/status/location-status.component';
import { getCurrentLanguageFromPath } from 'shared/utils/getCurrentLanguageFromPath';
import localeFr from '@angular/common/locales/fr';
import { CookieBannerComponent } from './cookie-banner/cookie-banner.component';
import { LicenseRedirectPopupComponent } from './license-redirect-popup/license-redirect-popup.component';
import { DebugModeModule } from './shared/debug-mode/debug-mode.module';
import { SECTION_BUILDER_PROVIDERS } from './shared/ai-diagnostic-streaming/section-builders/builder-providers';

registerLocaleData(localeFr);

@NgModule({
    declarations: [AppComponent, CookieBannerComponent, LicenseRedirectPopupComponent],
    imports: [
        BrowserModule,
        StationsServiceModule,
        AppRoutingModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        LayoutModule,
        CeProvidersModule,
        BrowserAnimationsModule,
        LocaleModule,
        DebugModeModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (http) => new ChargeyeTranslateLoader(http),
                deps: [HttpClient],
            },
        }),
    ],
    providers: [
        ...SECTION_BUILDER_PROVIDERS,
        // Dialogs open and close without animation. These options replace Material's
        // defaults wholesale, so start from them and change only the durations.
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: { ...new MatDialogConfig(), enterAnimationDuration: 0, exitAnimationDuration: 0 },
        },
        // Legacy tabs sized to their labels; MDC tabs stretch to fill the header by default.
        { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
        // Legacy toggles had a plain thumb; MDC draws a check or minus icon in it.
        { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { disableToggleValue: false, hideIcon: true } },
        // Legacy single-selection options had no check mark; MDC draws one on the
        // selected option. The other autocomplete defaults are Material's own.
        { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
        {
            provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
            useValue: {
                autoActiveFirstOption: false,
                autoSelectActiveOption: false,
                hideSingleSelectionIndicator: true,
                requireSelection: false,
            },
        },
        // Material 17 draws a check mark in the selected button toggle; the toggles
        // had none. Appearance stays Material's default ('standard').
        {
            provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'standard',
                hideSingleSelectionIndicator: true,
                hideMultipleSelectionIndicator: true,
            },
        },
        // CDK 21 opens every overlay as a native popover in the top layer, with its backdrop inside the
        // overlay, and renders select and autocomplete panels next to their trigger instead of in
        // .cdk-overlay-container. Keep CDK 20's rendering: every overlay in the overlay container.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        {
            provide: APP_BASE_HREF,
            useFactory: (s: PlatformLocation) => baseUrlFactory(s.getBaseHrefFromDOM()),
            deps: [PlatformLocation],
        },
        {
            provide: LOCALE_ID,
            useFactory: () => getCurrentLanguageFromPath(),
        },
        Title,
        LocationStatusComponent,
        CookieBannerComponent,
        provideHttpClient(withInterceptorsFromDi()),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
        // set up SVG icons
        setupIcons(matIconRegistry, domSanitizer);
    }
}

export function baseUrlFactory(baseUrl: string) {
    // const currentLanguage = getCurrentLanguageFromPath();

    // trim last slash from baseUrl
    const trimmed = trimLastSlashFromUrl(baseUrl);

    // add language to baseUrl is not needed as localized build takes care of assigning baseHref to the correct language already. Check angular.json > project > chargeye > i18n
    // if (currentLanguage && currentLanguage !== 'en') {
    //     trimmed += `/${currentLanguage}`;
    // }

    return trimmed;
}

function trimLastSlashFromUrl(baseUrl: string) {
    if (!baseUrl?.trim()) {
        return '';
    } else if (baseUrl[baseUrl.length - 1] === '/') {
        const trimmedUrl = baseUrl.substring(0, baseUrl.length - 1);
        return trimmedUrl;
    } else {
        return baseUrl;
    }
}
